import { Injectable } from '@angular/core';

/**
 * ClipboardProvider
 *
 * Centralized clipboard service for Case Manager.
 * Provides methods for copying text to the system clipboard.
 */
@Injectable({
  providedIn: 'root',
})
export class ClipboardProvider {
  /**
   * Copies text to the clipboard using the Clipboard API.
   * Falls back to execCommand if Clipboard API is not available.
   * @param text - The text to copy to clipboard
   * @returns Promise that resolves when text is copied, rejects on error
   */
  public async setClipboard(text: string): Promise<void> {
    try {
      // Modern Clipboard API (preferred)
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(text);
        return;
      }

      // Fallback for older browsers or non-secure contexts
      const textArea = document.createElement('textarea');
      textArea.value = text;
      textArea.style.position = 'fixed';
      textArea.style.left = '-999999px';
      textArea.style.top = '-999999px';
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();

      const successful = document.execCommand('copy');
      document.body.removeChild(textArea);

      if (!successful) {
        throw new Error('execCommand copy failed');
      }
    } catch (error) {
      throw new Error(`Failed to copy to clipboard: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }
}
